import React, { useState } from 'react'

import { useReactiveVar } from '@apollo/client'
import Tippy from '@tippyjs/react/headless'
import clsx from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'

import LogoutModal from '~/components/auth/LogoutModal'
import { HeaderTypes, UserHelpCenterUrl } from '~/constants'
import useTrackClick from '~/events/trackers/useTrackClick'
import { useGetUserQuery } from '~/graphql/generated/schema'
import { currentUserVar } from '~/graphql/reactive-vars'
import { useCommonTx } from '~/hooks/useTranslationForNamespace'
import BrandTitle from '~/images/icons/Brand/LumosityText.svg'
import ChevronDown from '~/images/icons/SystemOutlined/ChevronDown.svg'
import ChevronUp from '~/images/icons/SystemOutlined/ChevronUp.svg'
import Close from '~/images/icons/SystemOutlined/Close.svg'
import SettingsCog from '~/images/icons/SystemOutlined/Gear.svg'
import Help from '~/images/icons/SystemOutlined/HelpCircle.svg'
import LogOut from '~/images/icons/SystemOutlined/Logout.svg'
import Hamburger from '~/images/icons/SystemOutlined/Trigram.svg'

export interface TopNavProps {
  headerType?: HeaderTypes
  headerButtonComponent?: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

const NameMenu = ({ isWhite }: { isWhite: boolean }) => {
  const router = useRouter()
  const { data: userQueryData } = useGetUserQuery()
  const userName = userQueryData?.me?.firstName || '...'
  const t = useCommonTx('common')
  const [showLogoutModal, setShowLogoutModal] = useState(false)

  const { trackCta } = useTrackClick()

  const handleLogoutInApp = () => {
    trackCta({
      click_name: 'log_out',
      text: t('topNavDropdown.logOut'),
      type: 'link',
      destination: 'log_out',
    })
    setShowLogoutModal(true)
  }

  const [visible, setVisible] = useState(false)
  const show = () => setVisible(true)
  const hide = () => setVisible(false)

  const handleClickSettings = () => {
    trackCta({
      click_name: 'settings',
      destination: 'settings',
      text: t('topNavDropdown.settings'),
      type: 'link',
    })
    router.push('/settings')
  }

  const handleClickHelp = () => {
    trackCta({
      click_name: 'visit_helpdesk',
      destination: 'help_center',
      text: t('topNavDropdown.help'),
      type: 'link',
    })
    window.open(UserHelpCenterUrl, '_self')
  }
  return (
    <>
      <Tippy
        visible={visible}
        interactive={true}
        onClickOutside={hide}
        placement='bottom-start'
        render={(attrs) => (
          <div {...attrs}>
            <div className='rounded-2xl flex flex-col w-[300px] overflow-hidden bg-surface-default shadow-drop-shadow-2'>
              {[
                {
                  icon: <SettingsCog title='Settings' fontSize={'32px'} width={'32px'} height={'32px'} />,
                  text: t('topNavDropdown.settings'),
                  onClick: handleClickSettings,
                },
                {
                  icon: <Help title='Help' fontSize={'32px'} width={'32px'} height={'32px'} />,
                  text: t('topNavDropdown.help'),
                  onClick: handleClickHelp,
                },
                {
                  icon: <LogOut title='Log Out' fontSize={'32px'} width={'32px'} height={'32px'} />,
                  text: t('topNavDropdown.logOut'),
                  onClick: handleLogoutInApp,
                },
              ].map((item) => {
                return (
                  <button
                    onClick={item.onClick}
                    key={item.text}
                    className='w-full flex flex-row items-center flex-wrap h-[58px] px-4 py-3 text-text-subdued hover:text-text-default bg-surface-default hover:bg-surface-subdued'
                  >
                    {item.icon}
                    <div className='text-body-text-1 font-semibold ml-3'>{item.text}</div>
                  </button>
                )
              })}
            </div>
          </div>
        )}
      >
        <button
          onClick={visible ? hide : show}
          className={clsx('flex flex-row items-center gap-2 ml-6', isWhite ? 'text-white' : 'text-text-default')}
        >
          <div className='text-body-text-1 font-semibold hidden sm:block'>{userName}</div>
          {!visible && (
            <>
              <ChevronDown title='Dropdown' className='w-7 h-7 hidden sm:block' />
              <Hamburger title='Menu' className='w-10 h-10 block sm:hidden' />
            </>
          )}
          {visible && (
            <>
              <ChevronUp title='Dropdown' className='w-7 h-7 hidden sm:block' />
              <Close title='Close Menu' className='w-8 h-8 block sm:hidden' />
            </>
          )}
        </button>
      </Tippy>
      {showLogoutModal && <LogoutModal onClose={() => setShowLogoutModal(false)} />}
    </>
  )
}

interface TopNavigationProps {
  logoComponent?: React.ReactNode // sending this will replace the logo section
  className?: string
  style?: React.CSSProperties
  /**
   * many components are still passing showStreaks as true|false, we might need it in future
   * else we can remove this props as a separate exercise
   */
  showStreaks?: boolean
  showName?: boolean
  isSticky?: boolean
  isBlur?: boolean
  isTransparent?: boolean
  rightComponent?: React.ReactNode
  isWhite?: boolean
}
// Deprecate TopNav, the structure was complex & start using TopNavigation for better control
export const TopNavigation = ({
  logoComponent,
  className,
  style,
  isSticky = true,
  showName = true,
  isBlur = false,
  isTransparent = false,
  rightComponent = null,
  isWhite = false,
}: TopNavigationProps) => {
  const currentUser = useReactiveVar(currentUserVar)

  return (
    <div
      className={clsx(
        'h-[88px]',
        'flex flex-row justify-between items-center w-full px-4 sm:px-9',
        'border-b border-gray-200',
        isBlur ? 'backdrop-blur-[50px]' : '',
        isTransparent ? '' : 'bg-white',
        isSticky && 'sticky top-0 w-full z-50',
        className,
      )}
      style={style}
    >
      {logoComponent ? (
        logoComponent
      ) : (
        <Link href='/' passHref>
          {isWhite ? (
            <BrandTitle title='Lumosity' width={122} height={31} className='text-white' />
          ) : (
            <BrandTitle title='Lumosity' width={122} height={31} className='' />
          )}
        </Link>
      )}

      {currentUser ? (
        <div className='flex flex-row justify-end items-center'>
          {showName && <NameMenu isWhite={isWhite} />}
          {rightComponent}
        </div>
      ) : (
        <div className='flex flex-row justify-end items-center'>{rightComponent}</div>
      )}
    </div>
  )
}
